import { PlusOutlined } from "@ant-design/icons";
import { useNavigation } from "@refinedev/core";
import { Badge, Button, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { AdminPage } from "../../components/AdminPage";
import { formatAdminDateTime } from "../../lib/format";
import { getAccessToken } from "../../lib/staff";
import { type AdminStaffRole, ROLE_LABEL } from "../../lib/staff-types";
import { getTrpcClient } from "../../lib/trpc";
import { tamsThemeColors } from "../../theme";

type StaffRow = {
	createdAt: string;
	disabledAt: string | null;
	email: string | null;
	role: AdminStaffRole;
	userId: string;
};

export function StaffListPage() {
	const { create } = useNavigation();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [rows, setRows] = useState<StaffRow[]>([]);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			setLoading(true);
			setError(null);
			try {
				const accessToken = await getAccessToken();
				if (!accessToken) {
					throw new Error("サインインが必要です。");
				}
				const result = await getTrpcClient(accessToken).admin.listStaff.query();
				if (cancelled) return;
				setRows(result.items);
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error
							? err.message
							: "スタッフ一覧の取得に失敗しました。",
					);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<AdminPage
			title="スタッフ管理"
			subtitle="スタッフ一覧・招待"
			extra={
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => create("staff")}
				>
					招待
				</Button>
			}
			toolbar={
				<span className="admin-page-result-count">
					検索結果：{rows.length.toLocaleString("ja-JP")}件
				</span>
			}
		>
			{error ? (
				<div style={{ padding: 24 }}>
					<Typography.Text type="danger">{error}</Typography.Text>
				</div>
			) : (
				<Table
					rowKey="userId"
					size="middle"
					loading={loading}
					dataSource={rows}
					sticky
					pagination={
						rows.length > 20
							? {
									showSizeChanger: true,
									showTotal: (count) => `${count} 件`,
								}
							: false
					}
					columns={[
						{
							title: "メール",
							dataIndex: "email",
							render: (value: string | null) => value || "—",
						},
						{
							title: "ロール",
							dataIndex: "role",
							width: 200,
							render: (value: AdminStaffRole) =>
								value === "admin" ? (
									<Tag
										bordered={false}
										style={{
											color: tamsThemeColors.primary,
											background: tamsThemeColors.primarySoft,
										}}
									>
										{ROLE_LABEL[value]}
									</Tag>
								) : (
									<Tag bordered={false}>{ROLE_LABEL[value]}</Tag>
								),
						},
						{
							title: "状態",
							dataIndex: "disabledAt",
							width: 96,
							render: (value: string | null) =>
								value ? (
									<Badge status="default" text="無効" />
								) : (
									<Badge status="success" text="有効" />
								),
						},
						{
							title: "登録日時",
							dataIndex: "createdAt",
							width: 160,
							render: (value: string) => formatAdminDateTime(value),
						},
					]}
				/>
			)}
		</AdminPage>
	);
}
